/**
 * Buddy mascots · 12 variants (slot_t00_ftue_rewrite port · 7 JUN 2026).
 *
 * Source: `04_DESIGN/QA Studio Design System6.7/polished_prototype_riso/buddies.jsx`
 * (Genie claude-design chat · buddy rework). Byte-faithful port — SVG markup
 * untouched, only TS types + module exports layered on top (prototype used
 * `window.Buddies` global). Each variant takes { size, state }.
 * state ∈ 'idle' | 'hint' | 'happy' | 'sad' | 'sleep'.
 *
 * Palette: fixed RISO illustration hexes (как в прототипе и MS-01 SVG-вставках) —
 * mascots are printed-sticker artwork, deliberately NOT theme-reactive.
 *
 * `<BuddyMascot/>` — production wrapper (variant default 'bean' · no prototype
 * TweaksPanel context). Old `components/Buddy.tsx` остаётся для прежних
 * call-sites; app-wide swap = отдельное Genie-решение.
 */

export type BuddyState = 'idle' | 'hint' | 'happy' | 'sad' | 'sleep';
export interface BuddyVariantProps {
  size?: number;
  state?: BuddyState;
}

const RISO = {
  ink: '#1F2629',
  inkSoft: '#4F5A60',
  peach: '#F26B4A',
  peachSoft: '#FAC9B3',
  peachDeep: '#B73A1E',
  teal: '#2E8AA0',
  tealSoft: '#B6DCE5',
  tealDeep: '#1B6378',
  mustard: '#D8A317',
  mustardSoft: '#F2DC95',
  plum: '#6B3A5E',
  plumSoft: '#D7BFD0',
  green: '#4A8A5E',
  paper: '#FFFAEE',
  paperWarm: '#FBEFD9',
};

// ───────────────────────── Face primitives ─────────────────────────

// Eyes — 2 marks at cy, gap apart. State changes shape.
const Eyes = ({ cx = 50, cy = 50, gap = 14, r = 2.2, state, stroke = RISO.ink }: { cx?: number; cy?: number; gap?: number; r?: number; state?: BuddyState; stroke?: string }) => {
  const lx = cx - gap / 2, rx = cx + gap / 2;
  if (state === 'happy') {
    return (
      <g stroke={stroke} strokeWidth="1.8" fill="none" strokeLinecap="round">
        <path d={`M${lx - r * 1.4} ${cy + 0.4} Q${lx} ${cy - r * 1.8} ${lx + r * 1.4} ${cy + 0.4}`} />
        <path d={`M${rx - r * 1.4} ${cy + 0.4} Q${rx} ${cy - r * 1.8} ${rx + r * 1.4} ${cy + 0.4}`} />
      </g>
    );
  }
  if (state === 'sad') {
    return (
      <g fill={stroke} stroke={stroke}>
        <circle cx={lx} cy={cy + 0.5} r={r * 0.85} />
        <circle cx={rx} cy={cy + 0.5} r={r * 0.85} />
        {/* sad brows */}
        <g stroke={stroke} strokeWidth="1.5" fill="none" strokeLinecap="round">
          <path d={`M${lx - r * 1.6} ${cy - r * 2.4} L${lx + r * 1.6} ${cy - r * 1.4}`} />
          <path d={`M${rx + r * 1.6} ${cy - r * 2.4} L${rx - r * 1.6} ${cy - r * 1.4}`} />
        </g>
      </g>
    );
  }
  if (state === 'sleep') {
    return (
      <g stroke={stroke} strokeWidth="1.7" fill="none" strokeLinecap="round">
        <path d={`M${lx - r * 1.4} ${cy} Q${lx} ${cy + r * 1.4} ${lx + r * 1.4} ${cy}`} />
        <path d={`M${rx - r * 1.4} ${cy} Q${rx} ${cy + r * 1.4} ${rx + r * 1.4} ${cy}`} />
      </g>
    );
  }
  if (state === 'hint') {
    // looking up slightly — eyes offset upward
    return (
      <g fill={stroke}>
        <circle cx={lx} cy={cy - 0.6} r={r * 0.9} />
        <circle cx={rx} cy={cy - 0.6} r={r * 0.9} />
      </g>
    );
  }
  // idle
  return (
    <g fill={stroke}>
      <circle cx={lx} cy={cy} r={r * 0.9} />
      <circle cx={rx} cy={cy} r={r * 0.9} />
    </g>
  );
};

// Mouth — small mark below eyes
const Mouth = ({ cx = 50, cy = 58, w = 8, state, stroke = RISO.ink }: { cx?: number; cy?: number; w?: number; state?: BuddyState; stroke?: string }) => {
  if (state === 'happy') {
    return <path d={`M${cx - w / 2} ${cy - 0.5} Q${cx} ${cy + w * 0.55} ${cx + w / 2} ${cy - 0.5}`}
      stroke={stroke} strokeWidth="1.7" fill="none" strokeLinecap="round" />;
  }
  if (state === 'sad') {
    return <path d={`M${cx - w / 2} ${cy + 1} Q${cx} ${cy - w * 0.35} ${cx + w / 2} ${cy + 1}`}
      stroke={stroke} strokeWidth="1.7" fill="none" strokeLinecap="round" />;
  }
  if (state === 'sleep') {
    return <ellipse cx={cx + w / 2 + 1} cy={cy} rx={w * 0.18} ry={w * 0.1} fill={stroke} opacity="0.6" />;
  }
  if (state === 'hint') {
    return <ellipse cx={cx} cy={cy} rx={w * 0.18} ry={w * 0.22} fill={stroke} />;
  }
  // idle — small neutral arc
  return <path d={`M${cx - w / 3} ${cy} Q${cx} ${cy + w * 0.28} ${cx + w / 3} ${cy}`}
    stroke={stroke} strokeWidth="1.6" fill="none" strokeLinecap="round" />;
};

// State accessory — lightbulb / sparkle / tear / Zzz floating near head
const Accessory = ({ state, cx, cy }: { state?: BuddyState; cx: number; cy: number }) => {
  if (state === 'hint') {
    return (
      <g>
        <line x1={cx} y1={cy + 5} x2={cx} y2={cy + 8} stroke={RISO.ink} strokeWidth="1.3" strokeLinecap="round" />
        <path d={`M${cx - 4} ${cy} a4 4.5 0 1 1 8 0 c0 2 -1.4 3.2 -2 4 l-4 0 c-0.6 -0.8 -2 -2 -2 -4 z`}
          fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.4" />
        <line x1={cx - 2} y1={cy + 5} x2={cx + 2} y2={cy + 5} stroke={RISO.ink} strokeWidth="1" />
        {/* rays */}
        <g stroke={RISO.mustard} strokeWidth="1.4" strokeLinecap="round">
          <line x1={cx - 8} y1={cy - 2} x2={cx - 10.5} y2={cy - 3} />
          <line x1={cx + 8} y1={cy - 2} x2={cx + 10.5} y2={cy - 3} />
          <line x1={cx} y1={cy - 7} x2={cx} y2={cy - 9.5} />
        </g>
      </g>
    );
  }
  if (state === 'happy') {
    return (
      <g fill={RISO.mustard} stroke={RISO.ink} strokeWidth="0.8">
        <path d={`M${cx} ${cy - 3} L${cx + 1.4} ${cy} L${cx + 4} ${cy + 0.8} L${cx + 1.4} ${cy + 1.6} L${cx} ${cy + 4} L${cx - 1.4} ${cy + 1.6} L${cx - 4} ${cy + 0.8} L${cx - 1.4} ${cy} Z`} />
        <path d={`M${cx + 9} ${cy + 6} L${cx + 9.8} ${cy + 7.4} L${cx + 11.2} ${cy + 8} L${cx + 9.8} ${cy + 8.6} L${cx + 9} ${cy + 10} L${cx + 8.2} ${cy + 8.6} L${cx + 6.8} ${cy + 8} L${cx + 8.2} ${cy + 7.4} Z`} fill={RISO.peach} />
      </g>
    );
  }
  if (state === 'sleep') {
    return (
      <g fill={RISO.inkSoft} fontFamily="'Space Mono', monospace" fontWeight="700">
        <text x={cx} y={cy + 7} fontSize="6" textAnchor="middle">Z</text>
        <text x={cx + 4} y={cy + 2} fontSize="4.5" textAnchor="middle">z</text>
        <text x={cx + 7} y={cy - 2} fontSize="3.5" textAnchor="middle">z</text>
      </g>
    );
  }
  if (state === 'sad') {
    return (
      <path d={`M${cx} ${cy} l1.4 4 a1.6 1.6 0 1 1 -2.8 0 z`}
        fill={RISO.teal} stroke={RISO.ink} strokeWidth="0.8" />
    );
  }
  return null;
};

// Shared headphones — band + 2 cushions
const Headphones = ({ cx, top, span, bandW = 3.4, cushW = 5, cushH = 7, color = RISO.teal, cushColor = RISO.tealDeep }: { cx: number; top: number; span: number; bandW?: number; cushW?: number; cushH?: number; color?: string; cushColor?: string }) => (
  <g>
    <path d={`M${cx - span / 2} ${top + 4} Q${cx} ${top - 4} ${cx + span / 2} ${top + 4}`}
      stroke={color} strokeWidth={bandW} fill="none" strokeLinecap="round" />
    <path d={`M${cx - span / 2} ${top + 4} Q${cx} ${top - 4} ${cx + span / 2} ${top + 4}`}
      stroke={RISO.ink} strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.45" />
    <rect x={cx - span / 2 - cushW} y={top + 3} width={cushW * 2} height={cushH * 1.6} rx="2.2"
      fill={cushColor} stroke={RISO.ink} strokeWidth="1.4" />
    <rect x={cx + span / 2 - cushW} y={top + 3} width={cushW * 2} height={cushH * 1.6} rx="2.2"
      fill={cushColor} stroke={RISO.ink} strokeWidth="1.4" />
    <rect x={cx - span / 2 - cushW + 1.4} y={top + 4.5} width={cushW * 2 - 2.8} height={cushH * 1.3} rx="1.6"
      fill={RISO.peach} opacity="0.55" />
    <rect x={cx + span / 2 - cushW + 1.4} y={top + 4.5} width={cushW * 2 - 2.8} height={cushH * 1.3} rx="1.6"
      fill={RISO.peach} opacity="0.55" />
  </g>
);

// Shared mug — small ceramic with handle + steam
const Mug = ({ x, y, w = 12, h = 11, fill = RISO.paper, rim = RISO.peach, withSteam = true }: { x: number; y: number; w?: number; h?: number; fill?: string; rim?: string; withSteam?: boolean }) => (
  <g>
    {withSteam && (
      <g stroke={RISO.inkSoft} strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.55">
        <path d={`M${x + 3} ${y - 1} q-1.6 -2 0 -3.5 q1.6 -1.5 0 -3.5`} />
        <path d={`M${x + w - 3} ${y - 1} q-1.6 -2 0 -3.5 q1.6 -1.5 0 -3.5`} />
      </g>
    )}
    <rect x={x} y={y} width={w} height={h} rx="2" fill={fill} stroke={RISO.ink} strokeWidth="1.5" />
    <path d={`M${x + w} ${y + 2} q4 0 4 ${(h - 4) / 2} q0 ${(h - 4) / 2} -4 ${(h - 4) / 2}`}
      stroke={RISO.ink} strokeWidth="1.4" fill="none" />
    <line x1={x + 1.5} y1={y + 2.6} x2={x + w - 1.5} y2={y + 2.6} stroke={rim} strokeWidth="1.4" />
  </g>
);

// ───────────────────────── 1. BEAN — «Бин» ─────────────────────────
const Bean = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    {/* ground shadow */}
    <ellipse cx="50" cy="93" rx="26" ry="2.4" fill="rgba(0,0,0,0.14)" />
    {/* body — soft bean */}
    <path d="M28 52 C28 32, 72 32, 72 52 C76 78, 60 88, 50 88 C40 88, 24 78, 28 52 Z"
      fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="2" />
    {/* slight back-shape suggestion (teal) for risograph offset */}
    <path d="M32 52 C32 36, 68 36, 68 52 C71 73, 58 84, 50 84 C42 84, 29 73, 32 52 Z"
      fill={RISO.tealSoft} opacity="0.55" />
    {/* mug at chest */}
    <g transform="translate(0,2)">
      <Mug x={42} y={68} w={16} h={13} rim={RISO.peach} />
    </g>
    {/* face */}
    <g>
      <Eyes cx={50} cy={50} gap={14} r={2.6} state={state} />
      <Mouth cx={50} cy={59} w={9} state={state} />
    </g>
    {/* headphones */}
    <Headphones cx={50} top={30} span={36} />
    {/* state accessory floating top-right */}
    <Accessory state={state} cx={80} cy={26} />
  </svg>
);

// ───────────────────────── 2. OWL — «Хут» ─────────────────────────
const Owl = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="94" rx="26" ry="2.2" fill="rgba(0,0,0,0.14)" />
    {/* body — teardrop */}
    <path d="M26 56 C26 40, 36 28, 50 28 C64 28, 74 40, 74 56 C74 78, 64 90, 50 90 C36 90, 26 78, 26 56 Z"
      fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="2" />
    {/* belly plate */}
    <path d="M38 56 C38 70, 44 84, 50 84 C56 84, 62 70, 62 56 Z" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.3" />
    {/* feather marks on belly */}
    <g stroke={RISO.ink} strokeWidth="0.9" fill="none" opacity="0.5">
      <path d="M44 64 q2 -1.5 4 0" />
      <path d="M52 64 q2 -1.5 4 0" />
      <path d="M44 70 q2 -1.5 4 0" />
      <path d="M52 70 q2 -1.5 4 0" />
      <path d="M48 76 q2 -1.5 4 0" />
    </g>
    {/* wings */}
    <path d="M26 56 C24 70, 30 80, 36 78 L36 62 Z" fill={RISO.peach} stroke={RISO.ink} strokeWidth="1.5" />
    <path d="M74 56 C76 70, 70 80, 64 78 L64 62 Z" fill={RISO.peach} stroke={RISO.ink} strokeWidth="1.5" />
    {/* big eye discs (the "glasses") */}
    <circle cx="40" cy="48" r="10" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.8" />
    <circle cx="60" cy="48" r="10" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.8" />
    {/* eye contents — pupils replaced by state */}
    {state === 'happy' && (
      <g stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round">
        <path d="M35 48 Q40 43 45 48" /><path d="M55 48 Q60 43 65 48" />
      </g>
    )}
    {state === 'sad' && (
      <g fill={RISO.ink}>
        <circle cx="40" cy="50.5" r="2.4" /><circle cx="60" cy="50.5" r="2.4" />
        <g stroke={RISO.ink} strokeWidth="1.6" fill="none" strokeLinecap="round">
          <path d="M32 42 L46 47" /><path d="M68 42 L54 47" />
        </g>
      </g>
    )}
    {state === 'sleep' && (
      <g stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round">
        <path d="M35 50 Q40 54 45 50" /><path d="M55 50 Q60 54 65 50" />
      </g>
    )}
    {(state === 'idle' || state === 'hint') && (
      <g fill={RISO.ink}>
        <circle cx={state === 'hint' ? 41.5 : 40} cy={state === 'hint' ? 46 : 48} r="3" />
        <circle cx={state === 'hint' ? 61.5 : 60} cy={state === 'hint' ? 46 : 48} r="3" />
        <circle cx={state === 'hint' ? 42.5 : 41} cy={state === 'hint' ? 45 : 47} r="0.9" fill={RISO.paper} />
        <circle cx={state === 'hint' ? 62.5 : 61} cy={state === 'hint' ? 45 : 47} r="0.9" fill={RISO.paper} />
      </g>
    )}
    {/* beak */}
    <path d="M47 56 L50 62 L53 56 Z" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.3" />
    {/* tufts */}
    <path d="M32 32 L36 26 L40 32 Z" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.5" />
    <path d="M60 32 L64 26 L68 32 Z" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.5" />
    {/* headphones */}
    <Headphones cx={50} top={26} span={42} bandW={3} />
    {/* mug in talons */}
    <g transform="translate(0,-2)">
      <Mug x={56} y={82} w={11} h={9} withSteam={false} rim={RISO.teal} />
    </g>
    {/* feet */}
    <g stroke={RISO.ink} strokeWidth="1.5" fill={RISO.mustard}>
      <path d="M42 90 l-2 3 M42 90 l0 3 M42 90 l2 3" fill="none" strokeLinecap="round" />
    </g>
    <Accessory state={state} cx={82} cy={28} />
  </svg>
);

// ───────────────────────── 3. Q-STAMP — «Кью» ─────────────────────────
const Qstamp = ({ size = 140, state = 'idle' }: BuddyVariantProps) => {
  // perforated edges via dashed stroke
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
      <ellipse cx="50" cy="93" rx="28" ry="2.4" fill="rgba(0,0,0,0.14)" />
      {/* paper ticket */}
      <g transform="rotate(-3 50 50)">
        <rect x="14" y="18" width="72" height="64" rx="2" fill={RISO.paper}
          stroke={RISO.ink} strokeWidth="1.8" />
        {/* perforations */}
        <rect x="14" y="18" width="72" height="64" rx="2" fill="none"
          stroke={RISO.paper} strokeWidth="2.2" strokeDasharray="2.4 2.4" opacity="1" />
        <rect x="14" y="18" width="72" height="64" rx="2" fill="none"
          stroke={RISO.ink} strokeWidth="2.4" strokeDasharray="0.5 3" opacity="0.4" />
        {/* big Q */}
        <text x="48" y="68" fontFamily="'Fraunces', 'Recoleta', serif" fontWeight="700" fontSize="58"
          fill={RISO.peach} stroke={RISO.ink} strokeWidth="1.5" textAnchor="middle"
          style={{ paintOrder: 'stroke fill' }}>Q</text>
        {/* face inside Q's counter */}
        <g transform="translate(0,-4)">
          <Eyes cx={48} cy={56} gap={11} r={2.2} state={state} stroke={RISO.ink} />
          <Mouth cx={48} cy={64} w={7} state={state} />
        </g>
        {/* mono label */}
        <text x="50" y="80" fontFamily="'Space Mono', monospace" fontSize="6" fill={RISO.tealDeep}
          textAnchor="middle" letterSpacing="2">QA · PASSED</text>
        {/* corner mark */}
        <rect x="18" y="22" width="10" height="5" rx="1" fill={RISO.teal} stroke={RISO.ink} strokeWidth="1.1" />
        <text x="23" y="26" fontFamily="'Space Mono', monospace" fontSize="3.4" fill={RISO.paper}
          textAnchor="middle" fontWeight="700">N°07</text>
      </g>
      {/* headphone cushions peek over top */}
      <g transform="translate(0,-2)">
        <Headphones cx={50} top={10} span={56} bandW={3.2} cushW={4.4} cushH={6} />
      </g>
      <Accessory state={state} cx={85} cy={20} />
    </svg>
  );
};

// ───────────────────────── 4. TALLY — «Талли» (ASCII) ─────────────────────────
const Tally = ({ size = 140, state = 'idle' }: BuddyVariantProps) => {
  const faceFor = (s: BuddyState) => {
    if (s === 'happy') return '[ ^ ω ^ ]';
    if (s === 'sad')   return '[ ; _ ; ]';
    if (s === 'sleep') return '[ - . - ]';
    if (s === 'hint')  return '[ ° ◡ ° ]';
    return '[ • _ • ]';
  };
  const promptFor = (s: BuddyState) => {
    if (s === 'happy') return '$ ./close --ticket=BUG-127';
    if (s === 'sad')   return '$ pause --reason=burnout';
    if (s === 'sleep') return '$ sleep --until=tomorrow';
    if (s === 'hint')  return '$ hint --type=lightbulb';
    return '$ buddy --watching';
  };
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
      <ellipse cx="50" cy="93" rx="30" ry="2.4" fill="rgba(0,0,0,0.14)" />
      {/* terminal frame */}
      <rect x="10" y="20" width="80" height="64" rx="3"
        fill={RISO.ink} stroke={RISO.ink} strokeWidth="2" />
      {/* riso peach offset behind */}
      <rect x="13" y="23" width="80" height="64" rx="3"
        fill={RISO.peach} opacity="0.85" />
      <rect x="10" y="20" width="80" height="64" rx="3"
        fill={RISO.ink} stroke={RISO.ink} strokeWidth="2" />
      {/* title bar */}
      <rect x="10" y="20" width="80" height="9" rx="3" fill={RISO.tealDeep} />
      <g fill={RISO.peachSoft}>
        <circle cx="16" cy="24.5" r="1.4" />
        <circle cx="21" cy="24.5" r="1.4" />
        <circle cx="26" cy="24.5" r="1.4" />
      </g>
      <text x="50" y="26.8" fontFamily="'Space Mono', monospace" fontSize="4.6"
        fill={RISO.paper} textAnchor="middle" letterSpacing="1.4">buddy.exe — tally</text>
      {/* ASCII headphones */}
      <text x="50" y="42" fontFamily="'Space Mono', monospace" fontSize="7" fontWeight="700"
        fill={RISO.tealSoft} textAnchor="middle" letterSpacing="0.5">{`╭⌐■─■─⌐╮`}</text>
      {/* face */}
      <text x="50" y="56" fontFamily="'Space Mono', monospace" fontSize="9" fontWeight="700"
        fill={RISO.mustardSoft} textAnchor="middle" letterSpacing="0.4">{faceFor(state)}</text>
      {/* mug ASCII */}
      <text x="50" y="68" fontFamily="'Space Mono', monospace" fontSize="6"
        fill={RISO.peachSoft} textAnchor="middle">{`[ ☕ ]`}</text>
      {/* prompt */}
      <text x="14" y="79" fontFamily="'Space Mono', monospace" fontSize="3.6"
        fill={RISO.paperWarm} opacity="0.85">{promptFor(state)}</text>
      {/* blinking cursor */}
      <rect x="14" y="80.6" width="2" height="0.5" fill={RISO.paperWarm} opacity="0.9">
        <animate attributeName="opacity" values="0;1;1;0" dur="1.1s" repeatCount="indefinite" />
      </rect>
    </svg>
  );
};

// ───────────────────────── 5. HELM — «Хельм» (helmet) ─────────────────────────
const Helm = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <defs>
      <clipPath id="helm-visor">
        <ellipse cx="50" cy="50" rx="26" ry="24" />
      </clipPath>
    </defs>
    <ellipse cx="50" cy="93" rx="26" ry="2.4" fill="rgba(0,0,0,0.14)" />
    {/* shoulders / suit */}
    <path d="M22 90 C22 80, 32 74, 50 74 C68 74, 78 80, 78 90 Z"
      fill={RISO.peach} stroke={RISO.ink} strokeWidth="2" />
    {/* suit collar */}
    <rect x="40" y="70" width="20" height="8" rx="2" fill={RISO.peachDeep} stroke={RISO.ink} strokeWidth="1.5" />
    {/* helmet outer ring */}
    <circle cx="50" cy="50" r="30" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="2" />
    {/* visor */}
    <ellipse cx="50" cy="50" rx="26" ry="24" fill={RISO.tealSoft} stroke={RISO.ink} strokeWidth="1.6" />
    {/* visor reflection — tiny screens floating */}
    <g clipPath="url(#helm-visor)" opacity="0.75">
      <rect x="30" y="36" width="9" height="6" rx="1" fill={RISO.paper} stroke={RISO.tealDeep} strokeWidth="0.6" />
      <rect x="60" y="40" width="7" height="5" rx="1" fill={RISO.mustardSoft} stroke={RISO.tealDeep} strokeWidth="0.6" />
      <rect x="40" y="62" width="11" height="4" rx="1" fill={RISO.peachSoft} stroke={RISO.tealDeep} strokeWidth="0.6" />
      <g stroke={RISO.tealDeep} strokeWidth="0.5" opacity="0.55">
        <line x1="32" y1="44" x2="38" y2="44" /><line x1="62" y1="46" x2="66" y2="46" />
        <line x1="42" y1="68" x2="49" y2="68" />
      </g>
    </g>
    {/* visor highlight */}
    <path d="M32 38 Q42 28 56 32" stroke={RISO.paper} strokeWidth="2" fill="none" opacity="0.65" strokeLinecap="round" />
    {/* face inside visor */}
    <g opacity="0.95">
      <Eyes cx={50} cy={50} gap={12} r={2.2} state={state} />
      <Mouth cx={50} cy={58} w={8} state={state} />
    </g>
    {/* antenna */}
    <line x1="50" y1="20" x2="50" y2="14" stroke={RISO.ink} strokeWidth="2" strokeLinecap="round" />
    <circle cx="50" cy="13" r="2" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.4" />
    {/* side ports (headphones implicit in helmet) */}
    <circle cx="20" cy="50" r="4.5" fill={RISO.ink} stroke={RISO.ink} strokeWidth="1.2" />
    <circle cx="20" cy="50" r="2" fill={RISO.peach} />
    <circle cx="80" cy="50" r="4.5" fill={RISO.ink} stroke={RISO.ink} strokeWidth="1.2" />
    <circle cx="80" cy="50" r="2" fill={RISO.peach} />
    {/* mug in hand */}
    <g transform="translate(0,2)">
      <Mug x={64} y={84} w={11} h={8} withSteam={false} rim={RISO.peach} fill={RISO.paper} />
    </g>
    <Accessory state={state} cx={82} cy={20} />
  </svg>
);

// ───────────────────────── 6. QUILL — «Квилл» (geometric mentor) ─────────────────────────
const Quill = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="93" rx="26" ry="2.4" fill="rgba(0,0,0,0.14)" />
    {/* shoulders */}
    <path d="M22 92 C22 80, 32 74, 50 74 C68 74, 78 80, 78 92 Z"
      fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="2" />
    {/* sweater texture */}
    <g stroke={RISO.ink} strokeWidth="0.6" fill="none" opacity="0.4">
      <path d="M26 86 q4 -2 8 0 q4 2 8 0 q4 -2 8 0 q4 2 8 0 q4 -2 8 0" />
      <path d="M26 90 q4 -2 8 0 q4 2 8 0 q4 -2 8 0 q4 2 8 0 q4 -2 8 0" />
    </g>
    {/* neck */}
    <rect x="44" y="64" width="12" height="12" rx="2" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.6" />
    {/* head — tall rounded rectangle */}
    <path d="M26 32 C26 18, 74 18, 74 32 L74 56 C74 68, 64 74, 50 74 C36 74, 26 68, 26 56 Z"
      fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="2" />
    {/* riso teal offset on head */}
    <path d="M28 32 C28 22, 72 22, 72 32 L72 54 C72 64, 62 70, 50 70 C38 70, 28 64, 28 54 Z"
      fill={RISO.tealSoft} opacity="0.5" />
    {/* hair line / cap */}
    <path d="M26 32 C26 18, 74 18, 74 32 L74 36 C68 32, 60 30, 50 30 C40 30, 32 32, 26 36 Z"
      fill={RISO.plum} stroke={RISO.ink} strokeWidth="1.6" />
    {/* face */}
    <Eyes cx={50} cy={46} gap={16} r={2.6} state={state} />
    <Mouth cx={50} cy={58} w={9} state={state} />
    {/* nose tick */}
    <path d="M50 50 L49 54 L51 54 Z" fill="none" stroke={RISO.ink} strokeWidth="1" opacity="0.5" />
    {/* big headphones — band crosses head horizontally */}
    <path d="M22 46 Q22 36 36 36" stroke={RISO.ink} strokeWidth="2.2" fill="none" />
    <path d="M78 46 Q78 36 64 36" stroke={RISO.ink} strokeWidth="2.2" fill="none" />
    <rect x="16" y="42" width="10" height="14" rx="3" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.6" />
    <rect x="74" y="42" width="10" height="14" rx="3" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.6" />
    <rect x="18" y="44" width="6" height="10" rx="2" fill={RISO.peach} opacity="0.6" />
    <rect x="76" y="44" width="6" height="10" rx="2" fill={RISO.peach} opacity="0.6" />
    {/* mug held at chest */}
    <g>
      <Mug x={42} y={80} w={16} h={11} rim={RISO.peach} />
      {/* hands */}
      <ellipse cx="40" cy="86" rx="3.5" ry="3" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.4" />
      <ellipse cx="60" cy="86" rx="3.5" ry="3" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.4" />
    </g>
    <Accessory state={state} cx={82} cy={22} />
  </svg>
);

// ═════════════════════════════════════════════════════════════════════
// V2 — менее стандартные направления
// ═════════════════════════════════════════════════════════════════════

// ───────────────────────── 7. DUCK — «Утя» (rubber debugging duck) ─────────
const Duck = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="93" rx="30" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* water puddle */}
    <ellipse cx="50" cy="86" rx="28" ry="6" fill={RISO.tealSoft} stroke={RISO.ink} strokeWidth="1.4"/>
    <g stroke={RISO.tealDeep} strokeWidth="0.9" fill="none" opacity="0.7">
      <path d="M28 86 q3 -1.6 6 0"/>
      <path d="M44 88 q3 -1.6 6 0"/>
      <path d="M62 86 q3 -1.6 6 0"/>
    </g>
    {/* body — classic rubber duck silhouette */}
    <path d="M20 64 C20 50, 38 42, 58 46 C66 36, 74 36, 78 42 C82 48, 80 56, 74 58 C80 66, 80 78, 64 82 C42 86, 22 78, 20 64 Z"
      fill={RISO.mustardSoft} stroke={RISO.ink} strokeWidth="2"/>
    {/* riso peach offset on belly */}
    <path d="M22 64 C24 56, 38 50, 56 52 C58 58, 56 72, 64 78 C46 82, 26 76, 22 64 Z"
      fill={RISO.peachSoft} opacity="0.55"/>
    {/* head highlight */}
    <path d="M58 46 C66 38, 74 38, 78 44" stroke={RISO.paper} strokeWidth="1.4" fill="none" opacity="0.6"/>
    {/* beak */}
    {state === 'happy' ? (
      <g>
        <path d="M76 46 L92 44 L90 50 L74 50 Z" fill={RISO.peach} stroke={RISO.ink} strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M76 50 L92 52 L88 56 L75 54 Z" fill={RISO.peachDeep} stroke={RISO.ink} strokeWidth="1.5" strokeLinejoin="round"/>
      </g>
    ) : (
      <path d="M76 48 L90 46 L90 52 L75 53 Z" fill={RISO.peach} stroke={RISO.ink} strokeWidth="1.5" strokeLinejoin="round"/>
    )}
    {/* eye */}
    <g>
      {state === 'happy' && <path d="M65 42 Q68 38 71 42" stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round"/>}
      {state === 'sad' && (
        <g>
          <circle cx="68" cy="44" r="2.2" fill={RISO.ink}/>
          <path d="M64 39 L72 42" stroke={RISO.ink} strokeWidth="1.4" fill="none" strokeLinecap="round"/>
        </g>
      )}
      {state === 'sleep' && <path d="M64 44 Q68 47 72 44" stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round"/>}
      {state === 'hint' && (<g><circle cx="68" cy="42" r="2.4" fill={RISO.ink}/><circle cx="69" cy="41" r="0.7" fill={RISO.paper}/></g>)}
      {state === 'idle' && (<g><circle cx="68" cy="44" r="2.2" fill={RISO.ink}/><circle cx="69" cy="43" r="0.7" fill={RISO.paper}/></g>)}
    </g>
    {/* tail */}
    <path d="M20 60 L12 56 L14 64 L20 66 Z" fill={RISO.mustardSoft} stroke={RISO.ink} strokeWidth="1.6" strokeLinejoin="round"/>
    {/* headphones across head */}
    <path d="M52 36 Q66 18 80 32" stroke={RISO.ink} strokeWidth="2.6" fill="none" strokeLinecap="round"/>
    <rect x="46" y="32" width="9" height="11" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="78" y="28" width="9" height="11" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="48" y="34" width="5" height="7" rx="1.6" fill={RISO.peach} opacity="0.6"/>
    <rect x="80" y="30" width="5" height="7" rx="1.6" fill={RISO.peach} opacity="0.6"/>
    {/* tiny mug floating beside */}
    <g transform="translate(8,52)">
      <rect x="0" y="0" width="10" height="9" rx="1.5" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.4"/>
      <path d="M10 2 q3 0 3 2.5 q0 2.5 -3 2.5" stroke={RISO.ink} strokeWidth="1.3" fill="none"/>
      <line x1="1" y1="2.5" x2="9" y2="2.5" stroke={RISO.peach} strokeWidth="1.2"/>
    </g>
    <Accessory state={state} cx={84} cy={18}/>
  </svg>
);

// ───────────────────────── 8. BEETLE — «Жук» (the friendly bug) ─────────
const Beetle = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="93" rx="28" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* legs */}
    <g stroke={RISO.ink} strokeWidth="2" strokeLinecap="round" fill="none">
      <path d="M28 64 L18 58 L14 62"/>
      <path d="M28 72 L16 74"/>
      <path d="M28 80 L18 86"/>
      <path d="M72 64 L82 58 L86 62"/>
      <path d="M72 72 L84 74"/>
      <path d="M72 80 L82 86"/>
    </g>
    {/* shell — domed oval, split */}
    <ellipse cx="50" cy="70" rx="26" ry="20" fill={RISO.peach} stroke={RISO.ink} strokeWidth="2"/>
    <line x1="50" y1="50" x2="50" y2="90" stroke={RISO.ink} strokeWidth="1.5"/>
    {/* shell spots — geometric */}
    <g fill={RISO.ink}>
      <circle cx="40" cy="62" r="2.4"/>
      <circle cx="60" cy="62" r="2.4"/>
      <circle cx="36" cy="76" r="2"/>
      <circle cx="64" cy="76" r="2"/>
      <circle cx="50" cy="82" r="2.2"/>
    </g>
    {/* shell highlight */}
    <path d="M32 60 Q42 50 50 52" stroke={RISO.paper} strokeWidth="1.6" fill="none" opacity="0.5" strokeLinecap="round"/>
    {/* head */}
    <ellipse cx="50" cy="48" rx="14" ry="11" fill={RISO.ink} stroke={RISO.ink} strokeWidth="1.6"/>
    <ellipse cx="50" cy="47" rx="11" ry="8" fill={RISO.tealDeep}/>
    {/* antennae */}
    <g stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round">
      <path d="M42 38 Q38 28 32 26"/>
      <path d="M58 38 Q62 28 68 26"/>
    </g>
    <circle cx="32" cy="26" r="2.4" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.2"/>
    <circle cx="68" cy="26" r="2.4" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.2"/>
    {/* face — light eyes on dark head */}
    {state === 'idle' && (
      <g fill={RISO.paper}>
        <circle cx="45" cy="46" r="2"/><circle cx="55" cy="46" r="2"/>
        <circle cx="45.6" cy="45.4" r="0.7" fill={RISO.ink}/>
        <circle cx="55.6" cy="45.4" r="0.7" fill={RISO.ink}/>
      </g>
    )}
    {state === 'hint' && (
      <g fill={RISO.paper}>
        <circle cx="45" cy="44" r="2.2"/><circle cx="55" cy="44" r="2.2"/>
      </g>
    )}
    {state === 'happy' && (
      <g stroke={RISO.paper} strokeWidth="1.8" fill="none" strokeLinecap="round">
        <path d="M42 46 Q45 42 48 46"/><path d="M52 46 Q55 42 58 46"/>
      </g>
    )}
    {state === 'sad' && (
      <g>
        <g fill={RISO.paper}><circle cx="45" cy="48" r="1.6"/><circle cx="55" cy="48" r="1.6"/></g>
        <g stroke={RISO.paper} strokeWidth="1.4" fill="none" strokeLinecap="round">
          <path d="M40 42 L50 44"/><path d="M60 42 L50 44"/>
        </g>
      </g>
    )}
    {state === 'sleep' && (
      <g stroke={RISO.paper} strokeWidth="1.7" fill="none" strokeLinecap="round">
        <path d="M42 48 Q45 51 48 48"/><path d="M52 48 Q55 51 58 48"/>
      </g>
    )}
    {/* small smile on head bottom */}
    <Mouth cx={50} cy={54} w={6} state={state} stroke={RISO.paper}/>
    {/* mug nestled next to shell */}
    <g transform="translate(78,72)">
      <rect x="0" y="0" width="10" height="9" rx="1.5" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.4"/>
      <path d="M10 2 q3 0 3 2.5 q0 2.5 -3 2.5" stroke={RISO.ink} strokeWidth="1.3" fill="none"/>
      <line x1="1" y1="2.5" x2="9" y2="2.5" stroke={RISO.peach} strokeWidth="1.2"/>
    </g>
    <Accessory state={state} cx={18} cy={36}/>
  </svg>
);

// ───────────────────────── 9. CAPY — «Капи» (capybara) ─────────────
const Capy = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="93" rx="32" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* hot-tub mug */}
    <path d="M14 70 C14 62, 22 60, 50 60 C78 60, 86 62, 86 70 L86 84 C86 90, 78 92, 50 92 C22 92, 14 90, 14 84 Z"
      fill={RISO.paper} stroke={RISO.ink} strokeWidth="2"/>
    {/* rim band */}
    <path d="M14 70 C14 66, 22 64, 50 64 C78 64, 86 66, 86 70" stroke={RISO.peach} strokeWidth="2.4" fill="none"/>
    {/* steam */}
    <g stroke={RISO.inkSoft} strokeWidth="1.2" fill="none" opacity="0.55" strokeLinecap="round">
      <path d="M26 56 q-2 -3 0 -6 q2 -3 0 -6"/>
      <path d="M70 56 q-2 -3 0 -6 q2 -3 0 -6"/>
    </g>
    {/* handle */}
    <path d="M86 72 q8 0 8 8 q0 8 -8 8" stroke={RISO.ink} strokeWidth="2" fill="none"/>
    {/* water line */}
    <path d="M14 78 q9 4 18 0 q9 -4 18 0 q9 4 18 0 q9 -4 18 0" stroke={RISO.tealDeep} strokeWidth="1.2" fill="none" opacity="0.6"/>
    {/* capy head — barrel-shaped, low brow */}
    <path d="M28 60 C28 42, 42 36, 56 38 C72 40, 76 50, 76 60 Z"
      fill={RISO.peach} stroke={RISO.ink} strokeWidth="2"/>
    {/* riso teal offset */}
    <path d="M30 60 C30 44, 44 38, 56 40 C70 42, 74 50, 74 58 Z"
      fill={RISO.mustardSoft} opacity="0.5"/>
    {/* snout */}
    <ellipse cx="68" cy="56" rx="10" ry="6" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.5"/>
    {/* nostrils */}
    <g fill={RISO.ink}>
      <ellipse cx="72" cy="55" rx="0.9" ry="1.4"/>
      <ellipse cx="76" cy="55" rx="0.9" ry="1.4"/>
    </g>
    {/* ear (small) */}
    <path d="M36 38 Q34 32 40 32 L42 40 Z" fill={RISO.peachDeep} stroke={RISO.ink} strokeWidth="1.5"/>
    <path d="M38 38 Q38 34 40 35" fill="none" stroke={RISO.ink} strokeWidth="1" opacity="0.5"/>
    {/* eye — single, sleepy expression hallmark */}
    {(state === 'idle' || state === 'sad') && (
      <g>
        <ellipse cx="48" cy="48" rx="3" ry={state === 'sad' ? 2.4 : 2.8} fill={RISO.ink}/>
        <circle cx="49" cy="47" r="0.9" fill={RISO.paper}/>
      </g>
    )}
    {state === 'sleep' && (
      <path d="M44 50 Q48 53 52 50" stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round"/>
    )}
    {state === 'happy' && (
      <path d="M44 50 Q48 45 52 50" stroke={RISO.ink} strokeWidth="2" fill="none" strokeLinecap="round"/>
    )}
    {state === 'hint' && (
      <g>
        <ellipse cx="48" cy="46" rx="3" ry="3" fill={RISO.ink}/>
        <circle cx="49.2" cy="45" r="1" fill={RISO.paper}/>
      </g>
    )}
    {/* zen mouth — small line */}
    {state !== 'sleep' && state !== 'happy' && (
      <path d={state === 'sad' ? 'M64 60 Q67 58 70 60' : 'M62 60 L70 60'}
        stroke={RISO.ink} strokeWidth="1.6" fill="none" strokeLinecap="round"/>
    )}
    {/* headphone band */}
    <path d="M28 50 Q50 30 76 44" stroke={RISO.ink} strokeWidth="2.4" fill="none" strokeLinecap="round"/>
    <rect x="22" y="44" width="9" height="11" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="24" y="46" width="5" height="7" rx="1.5" fill={RISO.peach} opacity="0.6"/>
    {/* tangerine on head (capybara meme) */}
    <circle cx="44" cy="34" r="4" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.4"/>
    <path d="M44 30 L43 28 L46 28 Z" fill={RISO.green} stroke={RISO.ink} strokeWidth="1"/>
    <Accessory state={state} cx={86} cy={28}/>
  </svg>
);

// ───────────────────────── 10. LENS — «Линза» (sentient magnifier) ────────
const Lens = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="93" rx="26" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* handle */}
    <g transform="rotate(18 50 60)">
      <rect x="46" y="64" width="8" height="32" rx="3" fill={RISO.plum} stroke={RISO.ink} strokeWidth="2"/>
      <rect x="46" y="64" width="8" height="32" rx="3" fill="none" stroke={RISO.ink} strokeWidth="1" strokeDasharray="2 3" opacity="0.5"/>
      <rect x="44" y="60" width="12" height="6" rx="2" fill={RISO.plum} stroke={RISO.ink} strokeWidth="2"/>
    </g>
    {/* ring — bronze */}
    <circle cx="50" cy="44" r="28" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="2"/>
    <circle cx="50" cy="44" r="24" fill={RISO.mustardSoft} stroke={RISO.ink} strokeWidth="1.4"/>
    {/* lens glass */}
    <circle cx="50" cy="44" r="22" fill={RISO.tealSoft} stroke={RISO.ink} strokeWidth="1.4"/>
    {/* glass highlight */}
    <path d="M34 32 Q42 24 56 24" stroke={RISO.paper} strokeWidth="2.4" fill="none" opacity="0.75" strokeLinecap="round"/>
    <path d="M36 56 Q44 60 50 58" stroke={RISO.paper} strokeWidth="1.4" fill="none" opacity="0.5" strokeLinecap="round"/>
    {/* face INSIDE lens — slightly magnified */}
    <g>
      <Eyes cx={50} cy={42} gap={16} r={3.2} state={state}/>
      <Mouth cx={50} cy={54} w={11} state={state}/>
    </g>
    {/* headphones band over ring */}
    <path d="M22 38 Q50 8 78 38" stroke={RISO.ink} strokeWidth="2.4" fill="none" strokeLinecap="round"/>
    <rect x="16" y="34" width="9" height="11" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="75" y="34" width="9" height="11" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="18" y="36" width="5" height="7" rx="1.5" fill={RISO.peach} opacity="0.6"/>
    <rect x="77" y="36" width="5" height="7" rx="1.5" fill={RISO.peach} opacity="0.6"/>
    {/* tiny detail crosshair when hint */}
    {state === 'hint' && (
      <g stroke={RISO.peach} strokeWidth="1.4" fill="none" opacity="0.85">
        <circle cx="50" cy="44" r="20" strokeDasharray="2 3"/>
      </g>
    )}
    <Accessory state={state} cx={84} cy={18}/>
  </svg>
);

// ───────────────────────── 11. SLIP — «Слип» (triage receipt) ──────
const Slip = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="94" rx="28" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* paper slip */}
    <g transform="rotate(-2 50 50)">
      {/* zig-zag bottom + top */}
      <path d="M22 12 L22 88 L26 84 L30 88 L34 84 L38 88 L42 84 L46 88 L50 84 L54 88 L58 84 L62 88 L66 84 L70 88 L74 84 L78 88 L78 12 L74 16 L70 12 L66 16 L62 12 L58 16 L54 12 L50 16 L46 12 L42 16 L38 12 L34 16 L30 12 L26 16 Z"
        fill={RISO.paper} stroke={RISO.ink} strokeWidth="2" strokeLinejoin="round"/>
      {/* header band */}
      <rect x="22" y="20" width="56" height="12" fill={RISO.peach}/>
      <text x="50" y="29" fontFamily="'Space Mono', monospace" fontSize="6.5" fontWeight="700"
        fill={RISO.paper} textAnchor="middle" letterSpacing="2.5">BUDDY · #047</text>
      {/* face area */}
      <rect x="32" y="36" width="36" height="22" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.4"/>
      <g transform="translate(0,-2)">
        <Eyes cx={50} cy={46} gap={12} r={2.4} state={state}/>
        <Mouth cx={50} cy={54} w={8} state={state}/>
      </g>
      {/* perforation line */}
      <line x1="22" y1="62" x2="78" y2="62" stroke={RISO.ink} strokeWidth="0.8" strokeDasharray="1.5 2"/>
      {/* status fields */}
      <text x="26" y="70" fontFamily="'Space Mono', monospace" fontSize="4.5" fill={RISO.inkSoft} letterSpacing="0.5">STATUS</text>
      <text x="74" y="70" fontFamily="'Space Mono', monospace" fontSize="4.5" fontWeight="700" fill={RISO.tealDeep}
        textAnchor="end" letterSpacing="0.5">{
          state === 'happy' ? 'PASSED ✓' :
          state === 'sad' ? 'REOPEN' :
          state === 'hint' ? 'NOTE ADDED' :
          state === 'sleep' ? 'PAUSED' : 'WATCHING'
        }</text>
      <text x="26" y="78" fontFamily="'Space Mono', monospace" fontSize="4.5" fill={RISO.inkSoft} letterSpacing="0.5">SPRINT</text>
      <text x="74" y="78" fontFamily="'Space Mono', monospace" fontSize="4.5" fontWeight="700" fill={RISO.ink}
        textAnchor="end" letterSpacing="0.5">S-07</text>
      {/* peach barcode */}
      <g stroke={RISO.ink} strokeWidth="1" fill="none">
        <line x1="28" y1="82" x2="28" y2="86"/>
        <line x1="31" y1="82" x2="31" y2="86" strokeWidth="2"/>
        <line x1="35" y1="82" x2="35" y2="86"/>
        <line x1="37" y1="82" x2="37" y2="86" strokeWidth="2"/>
        <line x1="41" y1="82" x2="41" y2="86"/>
        <line x1="43" y1="82" x2="43" y2="86" strokeWidth="2"/>
        <line x1="47" y1="82" x2="47" y2="86"/>
        <line x1="50" y1="82" x2="50" y2="86" strokeWidth="2"/>
        <line x1="54" y1="82" x2="54" y2="86"/>
        <line x1="57" y1="82" x2="57" y2="86" strokeWidth="2"/>
        <line x1="61" y1="82" x2="61" y2="86"/>
        <line x1="64" y1="82" x2="64" y2="86" strokeWidth="2"/>
        <line x1="68" y1="82" x2="68" y2="86"/>
        <line x1="71" y1="82" x2="71" y2="86" strokeWidth="2"/>
      </g>
    </g>
    {/* clip on top */}
    <g transform="translate(46,6)">
      <rect x="0" y="0" width="8" height="8" rx="1.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
      <line x1="2" y1="2" x2="6" y2="2" stroke={RISO.paper} strokeWidth="1"/>
    </g>
    <Accessory state={state} cx={86} cy={20}/>
  </svg>
);

// ───────────────────────── 12. LAMP — «Лампа» (desk lamp Pixar-ish) ────
const Lamp = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="94" rx="30" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* light cone — soft */}
    <path d="M30 60 L20 90 L80 90 L70 60 Z" fill={RISO.mustardSoft} opacity="0.7"/>
    {/* book base */}
    <rect x="18" y="84" width="64" height="8" rx="1" fill={RISO.plum} stroke={RISO.ink} strokeWidth="1.8"/>
    <rect x="18" y="84" width="64" height="2" fill={RISO.paper} opacity="0.4"/>
    <line x1="22" y1="92" x2="22" y2="84" stroke={RISO.paper} strokeWidth="0.8" opacity="0.6"/>
    <line x1="78" y1="92" x2="78" y2="84" stroke={RISO.paper} strokeWidth="0.8" opacity="0.6"/>
    {/* mug on book */}
    <g transform="translate(60,76)">
      <rect x="0" y="0" width="10" height="8" rx="1.5" fill={RISO.paper} stroke={RISO.ink} strokeWidth="1.4"/>
      <path d="M10 2 q3 0 3 2 q0 2 -3 2" stroke={RISO.ink} strokeWidth="1.3" fill="none"/>
      <line x1="1" y1="2.5" x2="9" y2="2.5" stroke={RISO.peach} strokeWidth="1.2"/>
      <g stroke={RISO.inkSoft} strokeWidth="0.8" fill="none" opacity="0.55">
        <path d="M3 0 q-1 -1.5 0 -3"/>
        <path d="M7 0 q-1 -1.5 0 -3"/>
      </g>
    </g>
    {/* lamp base */}
    <ellipse cx="36" cy="84" rx="9" ry="3" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.6"/>
    {/* arm — bent */}
    <path d="M36 82 L36 60 L52 44" stroke={RISO.ink} strokeWidth="3.4" fill="none" strokeLinecap="round"/>
    <path d="M36 82 L36 60 L52 44" stroke={RISO.teal} strokeWidth="2" fill="none" strokeLinecap="round"/>
    <circle cx="36" cy="60" r="2.6" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.5"/>
    <circle cx="52" cy="44" r="2.6" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.5"/>
    {/* shade — trapezoid acting as head, tilted down */}
    <g transform="rotate(20 56 36)">
      <path d="M40 22 L74 22 L80 46 L34 46 Z" fill={RISO.peach} stroke={RISO.ink} strokeWidth="2" strokeLinejoin="round"/>
      {/* riso offset */}
      <path d="M42 24 L72 24 L77 44 L37 44 Z" fill={RISO.peachDeep} opacity="0.35"/>
      {/* face on shade */}
      <g transform="translate(0,2)">
        <Eyes cx={57} cy={32} gap={14} r={2.4} state={state} stroke={RISO.ink}/>
        <Mouth cx={57} cy={40} w={9} state={state}/>
      </g>
      {/* shade rim */}
      <line x1="40" y1="22" x2="74" y2="22" stroke={RISO.ink} strokeWidth="1.4"/>
      <line x1="34" y1="46" x2="80" y2="46" stroke={RISO.mustard} strokeWidth="2.4"/>
      {/* bulb glow under */}
      {state !== 'sleep' && <circle cx="57" cy="48" r="2.4" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.2"/>}
    </g>
    {/* headphones wrapped over shade ring */}
    <path d="M30 24 Q56 6 82 26" stroke={RISO.ink} strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.9"/>
    <rect x="24" y="22" width="8" height="10" rx="2.2" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="78" y="22" width="8" height="10" rx="2.2" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    {/* if sleep, dim cone */}
    {state === 'sleep' && <rect x="0" y="60" width="100" height="40" fill={RISO.paperWarm} opacity="0.55"/>}
    <Accessory state={state} cx={84} cy={12}/>
  </svg>
);

// ───────────────────────── 13. OCTO — «Ося» (parallel tester) ─────────
// Третий БАЗОВЫЙ бадди (решение Genie 2 JUL 2026 · акварельный концепт принят).
// Характер: спокойная многозадачность — восемь рук, ноль суеты. Сигнатура:
// полуприкрытые веки в idle («я всё вижу») + лупа/кружка/карандаш/стикер
// одновременно. Палитра plum — дополняет peach/mustard пары Хут+Капи.
const Octo = ({ size = 140, state = 'idle' }: BuddyVariantProps) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ display: 'block' }}>
    <ellipse cx="50" cy="94" rx="30" ry="2.4" fill="rgba(0,0,0,0.14)"/>
    {/* щупальца = «трубки»: чернильная подложка + plum-заливка сверху
        (двойной stroke даёт канонную чернильную обводку и держит 72px) */}
    {/* нижние завитки ×3 */}
    <g fill="none" strokeLinecap="round">
      <path d="M32 60 C28 74, 21 81, 16 77 C13 74, 17 70, 20 73" stroke={RISO.ink} strokeWidth="7.4"/>
      <path d="M32 60 C28 74, 21 81, 16 77 C13 74, 17 70, 20 73" stroke={RISO.plumSoft} strokeWidth="4.4"/>
      <path d="M50 63 C50 76, 45 85, 38 83 C34 81, 37 76, 41 78" stroke={RISO.ink} strokeWidth="7.4"/>
      <path d="M50 63 C50 76, 45 85, 38 83 C34 81, 37 76, 41 78" stroke={RISO.plumSoft} strokeWidth="4.4"/>
      <path d="M66 60 C70 74, 77 81, 82 77 C85 74, 81 70, 78 73" stroke={RISO.ink} strokeWidth="7.4"/>
      <path d="M66 60 C70 74, 77 81, 82 77 C85 74, 81 70, 78 73" stroke={RISO.plumSoft} strokeWidth="4.4"/>
    </g>
    {/* рука с лупой (вверх-влево) */}
    <g fill="none" strokeLinecap="round">
      <path d="M30 54 C22 52, 16 49, 14 44" stroke={RISO.ink} strokeWidth="6.6"/>
      <path d="M30 54 C22 52, 16 49, 14 44" stroke={RISO.plumSoft} strokeWidth="3.8"/>
    </g>
    <circle cx="13" cy="38" r="4.8" fill={RISO.tealSoft} stroke={RISO.ink} strokeWidth="1.7"/>
    <circle cx="13" cy="38" r="2.9" fill="none" stroke={RISO.paper} strokeWidth="0.9" opacity="0.8"/>
    <line x1="13.4" y1="42.8" x2="14" y2="45.5" stroke={RISO.ink} strokeWidth="1.8" strokeLinecap="round"/>
    {/* рука с карандашом (вверх-вправо) */}
    <g fill="none" strokeLinecap="round">
      <path d="M70 54 C78 52, 84 49, 86 44" stroke={RISO.ink} strokeWidth="6.6"/>
      <path d="M70 54 C78 52, 84 49, 86 44" stroke={RISO.plumSoft} strokeWidth="3.8"/>
    </g>
    <g transform="rotate(-24 87 38)">
      <rect x="84.6" y="31" width="4.6" height="12" rx="1" fill={RISO.mustard} stroke={RISO.ink} strokeWidth="1.3"/>
      <path d="M84.6 43 L86.9 47.5 L89.2 43 Z" fill={RISO.peachSoft} stroke={RISO.ink} strokeWidth="1.1" strokeLinejoin="round"/>
    </g>
    {/* купол */}
    <path d="M26 62 C24 36, 34 20, 50 20 C66 20, 76 36, 74 62 C66 66, 34 66, 26 62 Z"
      fill={RISO.plumSoft} stroke={RISO.ink} strokeWidth="2"/>
    {/* riso offset */}
    <path d="M29 60 C28 38, 37 23, 50 23 C63 23, 72 38, 71 60 C63 63, 37 63, 29 60 Z"
      fill={RISO.plum} opacity="0.18"/>
    {/* блик */}
    <path d="M36 30 Q42 23 52 23" stroke={RISO.paper} strokeWidth="1.6" fill="none" opacity="0.65" strokeLinecap="round"/>
    {/* лицо */}
    <Eyes cx={50} cy={44} gap={16} r={2.6} state={state}/>
    {/* сигнатура Оси: полуприкрытые веки в idle («я всё вижу») */}
    {state === 'idle' && (
      <g stroke={RISO.ink} strokeWidth="1.6" fill="none" strokeLinecap="round">
        <path d="M39 41.6 L45 41.6"/>
        <path d="M55 41.6 L61 41.6"/>
      </g>
    )}
    <Mouth cx={50} cy={53} w={8} state={state}/>
    {/* наушники — кастомная дуга ЧЕРЕЗ купол (хелпер даёт «шапку» на куполе) */}
    <path d="M26 38 Q50 8 74 38" stroke={RISO.ink} strokeWidth="2.6" fill="none" strokeLinecap="round"/>
    <rect x="20" y="34" width="9" height="12" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="71" y="34" width="9" height="12" rx="2.5" fill={RISO.tealDeep} stroke={RISO.ink} strokeWidth="1.4"/>
    <rect x="22" y="36" width="5" height="8" rx="1.6" fill={RISO.peach} opacity="0.6"/>
    <rect x="73" y="36" width="5" height="8" rx="1.6" fill={RISO.peach} opacity="0.6"/>
    {/* кружка на «земле» у левого завитка */}
    <g transform="translate(38,70)">
      <Mug x={0} y={0} w={12} h={10} withSteam={state !== 'sleep'}/>
    </g>
    {/* стикер под правым завитком */}
    <g transform="rotate(-6 63 84)">
      <rect x="57" y="80" width="12" height="9" rx="1" fill={RISO.mustardSoft} stroke={RISO.ink} strokeWidth="1.3"/>
      <line x1="59" y1="83" x2="67" y2="83" stroke={RISO.ink} strokeWidth="0.9" opacity="0.6"/>
      <line x1="59" y1="86" x2="65" y2="86" stroke={RISO.ink} strokeWidth="0.9" opacity="0.5"/>
    </g>
    <Accessory state={state} cx={84} cy={14}/>
  </svg>
);

export const BUDDY_VARIANTS = {
  bean: Bean,
  owl: Owl,
  q: Qstamp,
  tally: Tally,
  helm: Helm,
  quill: Quill,
  duck: Duck,
  beetle: Beetle,
  capy: Capy,
  lens: Lens,
  slip: Slip,
  lamp: Lamp,
  octo: Octo,
} as const;

export type BuddyVariant = keyof typeof BUDDY_VARIANTS;

export { RISO as BUDDY_RISO };

/**
 * Ключевые маскоты курса — ТРИ базовых бадди в ротации: Хут (сова) · Капи
 * (капибара) · Ося (осьминожка) — решение Genie 2 JUL 2026 (Утя из базовых
 * выведена: неодушевлённая игрушка не держит характер в иллюстрациях).
 * Ротация по дню года (mod 3), детерминированно, чтобы в рамках одного дня
 * ВСЕ места (peek + footer + будущие) показывали одного персонажа — Buddy
 * не должна менять облик между углами одного экрана.
 */
const ROTATION: readonly BuddyVariant[] = ['owl', 'capy', 'octo'];
export function rotatingBuddyVariant(now: Date = new Date()): BuddyVariant {
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  return ROTATION[dayOfYear % ROTATION.length] ?? 'owl';
}

/**
 * Production BuddyMascot — variant-aware wrapper (prototype app.jsx L65-77,
 * без BuddyVariantContext/TweaksPanel: variant приходит пропом). Default —
 * дневная ротация сова/капибара/осьминожка (rotatingBuddyVariant).
 */
export function BuddyMascot({
  size = 110,
  variant,
  state = 'idle',
}: BuddyVariantProps & { variant?: BuddyVariant }) {
  const Comp = BUDDY_VARIANTS[variant ?? rotatingBuddyVariant()] ?? BUDDY_VARIANTS.owl;
  return <Comp size={size} state={state} />;
}
